import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { Package, Plus, Pencil, Trash2, Download, Upload, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Spinner, ConfirmationModal } from '@crc-evans/ui-components';
import { message, Pagination } from 'antd';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';
import '../../pages/technical/HeatProfilePage.css';
import './DPRSettingsPipelinesTab.css';
import { useHeatStyleColumnFacets } from '../technical/heatStyleColumnFacets';
import {
  fetchConsumables,
  fetchConsumableCategories,
  addConsumables,
  updateConsumables,
  deleteConsumable,
  uploadConsumablesAndMaterialFile,
} from '../../redux/consumable/action';
import './DPRSettingsConsumablesTab.css';
import { ProjectTabTableEmpty } from '../project/ProjectTabTableEmpty';

const capitalizeWords = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const CONSUMABLE_FACET_KEYS = [
  '__categoryDisplay',
  '__nameDisplay',
  '__productCodeDisplay',
  '__qtyStock',
  '__qtyUsed',
  '__qtyRemaining',
  '__safetyStock',
  '__unit',
  '__criticality',
];

const CONSUMABLE_COLUMN_FILTERS_EMPTY = CONSUMABLE_FACET_KEYS.reduce((acc, k) => {
  acc[k] = '';
  return acc;
}, {});

function enrichConsumableRowForFacets(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    __categoryDisplay: row.category ?? '—',
    __nameDisplay: capitalizeWords(row.name || '') || '—',
    __productCodeDisplay: (row.productCode || '').toUpperCase() || '—',
    __qtyStock:
      row.quantityInStock != null && row.quantityInStock !== '' ? String(row.quantityInStock) : '—',
    __qtyUsed:
      row.totalQuantityUsed != null && row.totalQuantityUsed !== '' ? String(row.totalQuantityUsed) : '—',
    __qtyRemaining:
      row.quantityRemaining != null && row.quantityRemaining !== '' ? String(row.quantityRemaining) : '—',
    __safetyStock: row.safetyStock != null && row.safetyStock !== '' ? String(row.safetyStock) : '—',
    __unit: row.unit ?? '—',
    __criticality: row.criticality ?? '—',
  };
}

// Reusable select dropdown - same styling as other DPR Settings tabs
const SimpleSelect = ({
  value,
  options = [],
  onChange,
  placeholder = 'Select',
  disabled = false,
  open,
  onOpenChange,
  containerRef,
  optionLabel = 'label',
  optionValue = 'value',
  dropdownDirection = 'down',
}) => {
  const selected = options.find(
    (o) => String(o[optionValue]) === String(value)
  );
  const displayLabel = selected ? selected[optionLabel] : '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef?.current && !containerRef.current.contains(e.target))
        onOpenChange?.(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [containerRef, onOpenChange]);

  return (
    <div ref={containerRef} className="dpr-settings-consumables-select-wrap">
      <div
        className="dpr-settings-consumables-select-trigger"
        onClick={() => !disabled && onOpenChange?.(!open)}
        style={{
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span className="dpr-settings-consumables-select-value">
          {displayLabel || placeholder}
        </span>
        {open ? (
          <ChevronUp size={18} color="#6B7280" />
        ) : (
          <ChevronDown size={18} color="#6B7280" />
        )}
      </div>
      {open && (
        <div
          className={`dpr-settings-consumables-select-dropdown ${
            dropdownDirection === 'up' ? 'dpr-settings-consumables-select-dropdown--up' : ''
          }`}
        >
          {options.map((opt) => (
            <div
              key={opt[optionValue]}
              className="dpr-settings-consumables-select-opt"
              onClick={() => {
                onChange(opt[optionValue]);
                onOpenChange(false);
              }}
            >
              {opt[optionLabel]}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DPRSettingsConsumablesTab = ({
  selectedProject,
  consumables,
  categoryData,
  isConsumablesLoading,
  isConsumableDeleteLoading,
  isUploadConsumablesAndMaterialConsumptions,
  isConsumablesUpdating,
  isConsumableCategoryLoading,
  projectTeamMembersAccessList,
  userAccessList,
  onFetchConsumables,
  onFetchConsumableCategories,
  onAddConsumables,
  onUpdateConsumables,
  onDeleteConsumable,
  onUploadConsumableMaterialFiles,
}) => {
  const [searchKey, setSearchKey] = useState('');
  const [heatColumnFilters, setHeatColumnFilters] = useState(() => ({ ...CONSUMABLE_COLUMN_FILTERS_EMPTY }));
  const [consumableSort, setConsumableSort] = useState({
    key: '__categoryDisplay',
    desc: false,
  });
  const [consPage, setConsPage] = useState(1);
  const [consPageSize, setConsPageSize] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [disableFileUpload, setDisableFileUpload] = useState(false);

  // Form state for Add/Edit
  const [formCategory, setFormCategory] = useState('');
  const [formName, setFormName] = useState('');
  const [formProductCode, setFormProductCode] = useState('');
  const [formInitialQuantity, setFormInitialQuantity] = useState('');
  const [formSafetyStock, setFormSafetyStock] = useState('');
  const [formUnit, setFormUnit] = useState('');
  const [formCriticality, setFormCriticality] = useState('');
  const [filteredCatg, setFilteredCatg] = useState([]);
  const [filteredProductCode, setFilteredProductCode] = useState([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);
  const [productCodeOpen, setProductCodeOpen] = useState(false);
  const [unitOpen, setUnitOpen] = useState(false);
  const [criticalityOpen, setCriticalityOpen] = useState(false);
  const categoryRef = useRef(null);
  const nameRef = useRef(null);
  const productCodeRef = useRef(null);
  const unitRef = useRef(null);
  const criticalityRef = useRef(null);

  // Toolbar search (multi-token) — same pattern as Material Consumption / Equipment
  const consumableSearchFiltered = useMemo(() => {
    const full = Array.isArray(consumables) ? consumables : [];
    const needle = searchKey.trim().toLowerCase();
    if (!needle) return full;
    const tokens = needle.split(/\s+/).filter(Boolean);
    return full.filter((item) => {
      const haystack = [
        item?.category,
        item?.name,
        item?.productCode,
        item?.quantityInStock,
        item?.totalQuantityUsed,
        item?.quantityRemaining,
        item?.safetyStock,
        item?.unit,
        item?.criticality,
      ]
        .map((x) => (x === undefined || x === null ? '' : String(x)).toLowerCase())
        .join(' ');
      return tokens.every((t) => haystack.includes(t));
    });
  }, [consumables, searchKey]);

  const consumableFacetSource = useMemo(
    () => consumableSearchFiltered.map((r) => enrichConsumableRowForFacets({ ...r })),
    [consumableSearchFiltered]
  );

  const handleHeatColumnFilterChange = useCallback((key, value) => {
    setHeatColumnFilters((prev) => ({ ...prev, [key]: value }));
    setConsPage(1);
  }, []);

  const consumableFacetRef = useRef([]);
  consumableFacetRef.current = consumableFacetSource;
  const fetchConsumableFacetRows = useCallback(async () => {
    const list = consumableFacetRef.current;
    return Array.isArray(list) ? [...list] : [];
  }, []);

  const consumableFacetSearchCriteria = useMemo(() => ({}), []);

  const { applyFacetClientFilter, renderTextColumnFilterTrigger } = useHeatStyleColumnFacets({
    enabled: Boolean(selectedProject?.id),
    filterableColumns: CONSUMABLE_FACET_KEYS,
    listCount: consumableFacetSource.length,
    projectList: consumableFacetSource,
    searchCriteria: consumableFacetSearchCriteria,
    columnFilters: heatColumnFilters,
    onColumnFilterChange: handleHeatColumnFilterChange,
    fetchFacetRows: fetchConsumableFacetRows,
  });

  const lc = (v) => (v == null ? '' : String(v)).trim().toLowerCase();

  const filteredAndFacetedConsumables = useMemo(() => {
    let rows = [...consumableFacetSource];
    CONSUMABLE_FACET_KEYS.forEach((key) => {
      const raw = (heatColumnFilters[key] || '').trim();
      if (!raw) return;
      const q = lc(raw);
      rows = rows.filter((row) => lc(get(row, key)).includes(q));
    });
    return applyFacetClientFilter(rows);
  }, [consumableFacetSource, heatColumnFilters, applyFacetClientFilter]);

  const handleConsumableSortClick = useCallback((field) => {
    setConsumableSort((prev) =>
      prev.key === field ? { ...prev, desc: !prev.desc } : { key: field, desc: false }
    );
  }, []);

  const renderConsumableSortCaret = useCallback(
    (field) =>
      consumableSort.key === field ? (
        consumableSort.desc ? (
          <CaretDownOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        ) : (
          <CaretUpOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        )
      ) : (
        <CaretUpOutlined
          className="equipment-type-sort-dir-icon doc-control-sort-caret-inactive"
          aria-hidden
        />
      ),
    [consumableSort.key, consumableSort.desc]
  );

  const renderConsumableHeader = useCallback(
    (labelNode, dataIndex, sortField, { filterable = true, headerAlign = 'center' } = {}) => {
      if (!filterable) {
        return (
          <div className={`dpr-pipelines-header-static dpr-pipelines-header-static--${headerAlign}`}>
            {labelNode}
          </div>
        );
      }
      return (
        <div className={`dpr-pipelines-heat-header dpr-pipelines-heat-header--${headerAlign}`}>
          <div className="column-header-text-wrapper spares-column-header-sortable dpr-pipelines-heat-title-row">
            <span
              className="dpr-pipelines-heat-label dpr-pipelines-heat-label--sortable"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleConsumableSortClick(sortField);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleConsumableSortClick(sortField);
                }
              }}
            >
              {labelNode}
            </span>
            <span
              className="custom-sort-icon"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleConsumableSortClick(sortField);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleConsumableSortClick(sortField);
                }
              }}
            >
              {renderConsumableSortCaret(sortField)}
            </span>
            {renderTextColumnFilterTrigger(dataIndex)}
          </div>
        </div>
      );
    },
    [handleConsumableSortClick, renderConsumableSortCaret, renderTextColumnFilterTrigger]
  );

  const sortedConsumableDisplayList = useMemo(() => {
    const rows = [...filteredAndFacetedConsumables];
    const cmpStr = (a, b, key) =>
      String(get(a, key) ?? '')
        .trim()
        .toLowerCase()
        .localeCompare(String(get(b, key) ?? '').trim().toLowerCase(), undefined, {
          sensitivity: 'base',
          numeric: true,
        });
    const cmpNum = (a, b, camel) => {
      const na = Number(get(a, camel));
      const nb = Number(get(b, camel));
      const aOk = Number.isFinite(na);
      const bOk = Number.isFinite(nb);
      if (!aOk && !bOk) return 0;
      if (!aOk) return 1;
      if (!bOk) return -1;
      return na - nb;
    };
    const tieBreak = (a, b) => {
      const idA = a?.id != null ? Number(a.id) : NaN;
      const idB = b?.id != null ? Number(b.id) : NaN;
      if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) return idA < idB ? -1 : 1;
      return String(a?.id ?? '').localeCompare(String(b?.id ?? ''), undefined, { numeric: true });
    };
    const { key: sortKey, desc: sortDesc } = consumableSort;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case '__categoryDisplay':
        case '__nameDisplay':
        case '__productCodeDisplay':
        case '__qtyStock':
        case '__qtyUsed':
        case '__qtyRemaining':
        case '__safetyStock':
        case '__unit':
        case '__criticality':
          cmp = cmpStr(a, b, sortKey);
          break;
        case 'quantityInStock':
          cmp = cmpNum(a, b, 'quantityInStock');
          break;
        case 'totalQuantityUsed':
          cmp = cmpNum(a, b, 'totalQuantityUsed');
          break;
        case 'quantityRemaining':
          cmp = cmpNum(a, b, 'quantityRemaining');
          break;
        case 'safetyStock':
          cmp = cmpNum(a, b, 'safetyStock');
          break;
        default:
          cmp = 0;
      }
      if (cmp === 0) cmp = tieBreak(a, b);
      return sortDesc ? -cmp : cmp;
    });
    return rows;
  }, [filteredAndFacetedConsumables, consumableSort]);

  const list = sortedConsumableDisplayList;

  const paginatedList = useMemo(() => {
    const start = (consPage - 1) * consPageSize;
    return list.slice(start, start + consPageSize);
  }, [list, consPage, consPageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(list.length / consPageSize) || 1);
    if (consPage > maxPage) setConsPage(maxPage);
  }, [list.length, consPage, consPageSize]);
  const categoryDataList = (() => {
    if (!Array.isArray(categoryData) || !categoryData.length) return [];
    const seen = new Set();
    return categoryData.filter((item) => {
      const key = item?.category;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();
  const unitDataList = (() => {
    if (!Array.isArray(categoryData) || !categoryData.length) return [];
    const seen = new Set();
    return categoryData.filter((item) => {
      const key = item?.unit;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  const isWriteAccess = (() => {
    const groups =
      userAccessList?.length > 0
        ? userAccessList
        : (() => {
            try {
              return JSON.parse(localStorage.getItem('UserGroup') || '[]');
            } catch (e) {
              return [];
            }
          })();
    if (Array.isArray(groups) && (groups.includes('admin') || groups.includes('super-admin')))
      return true;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return false;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !projectTeamMembersAccessList?.[0]?.tabPermissions?.Consumables?.write
    )
      return false;
    return true;
  })();

  // Fetch full list and categories when project changes (search + pagination reset client-side)
  useEffect(() => {
    if (!selectedProject?.id) return;
    setConsPage(1);
    setSearchKey('');
    setHeatColumnFilters({ ...CONSUMABLE_COLUMN_FILTERS_EMPTY });
    setConsumableSort({ key: '__categoryDisplay', desc: false });
    onFetchConsumableCategories();
    onFetchConsumables(selectedProject);
  }, [selectedProject?.id]);

  const prevConsumableDeleteLoading = useRef(false);
  useEffect(() => {
    const wasDeleting = prevConsumableDeleteLoading.current;
    prevConsumableDeleteLoading.current = isConsumableDeleteLoading;
    if (wasDeleting && !isConsumableDeleteLoading) {
      setIsDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  }, [isConsumableDeleteLoading]);

  const prevConsumablesLoading = useRef(false);
  const prevConsumablesUpdating = useRef(false);
  useEffect(() => {
    const wasLoading = prevConsumablesLoading.current;
    const wasUpdating = prevConsumablesUpdating.current;
    prevConsumablesLoading.current = isConsumablesLoading;
    prevConsumablesUpdating.current = isConsumablesUpdating;
    if ((wasLoading && !isConsumablesLoading) || (wasUpdating && !isConsumablesUpdating)) {
      setShowAddModal(false);
      setEditRecord(null);
      resetForm();
    }
  }, [isConsumablesLoading, isConsumablesUpdating]);

  useEffect(() => {
    if (!isUploadConsumablesAndMaterialConsumptions) {
      setFileList([]);
      setBulkUploadModal(false);
      setDisableFileUpload(false);
    }
  }, [isUploadConsumablesAndMaterialConsumptions]);

  const resetForm = () => {
    setFormCategory('');
    setFormName('');
    setFormProductCode('');
    setFormInitialQuantity('');
    setFormSafetyStock('');
    setFormUnit('');
    setFormCriticality('');
    setFilteredCatg([]);
    setFilteredProductCode([]);
  };

  const openAddModal = () => {
    if (!selectedProject) {
      message.warning('Select a project to add consumables.');
      return;
    }
    setEditRecord(null);
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (record) => {
    setEditRecord(record);
    setFormCategory(record?.category ?? '');
    setFormName(record?.name ?? '');
    setFormProductCode(record?.productCode ?? '');
    setFormInitialQuantity(String(record?.quantityInStock ?? ''));
    setFormSafetyStock(String(record?.safetyStock ?? ''));
    setFormUnit(record?.unit ?? '');
    setFormCriticality(record?.criticality ?? '');
    const catArr = Array.isArray(categoryData)
      ? categoryData.filter((item) => item?.category === record?.category)
      : [];
    const uniqueName = new Set();
    const nameFiltered = catArr.filter((item) => {
      const key = item?.name;
      if (uniqueName.has(key)) return false;
      uniqueName.add(key);
      return true;
    });
    setFilteredCatg(nameFiltered);
    const codeArr = nameFiltered.filter((item) => item?.name === record?.name);
    const uniqueCode = new Set();
    const codeFiltered = codeArr.filter((item) => {
      const key = item?.productCode;
      if (uniqueCode.has(key)) return false;
      uniqueCode.add(key);
      return true;
    });
    setFilteredProductCode(codeFiltered);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setEditRecord(null);
    resetForm();
  };

  const onCategoryChange = (value) => {
    setFormCategory(value);
    setFormName('');
    setFormProductCode('');
    setFilteredProductCode([]);
    if (!value) {
      setFilteredCatg([]);
      return;
    }
    const catArr = (categoryData || []).filter((item) => item?.category === value);
    const uniqueName = new Set();
    const arr = catArr.filter((item) => {
      const key = item?.name;
      if (uniqueName.has(key)) return false;
      uniqueName.add(key);
      return true;
    });
    setFilteredCatg(arr);
  };

  const onNameChange = (value) => {
    setFormName(value);
    setFormProductCode('');
    if (!value) {
      setFilteredProductCode([]);
      return;
    }
    const arr = (filteredCatg || []).filter((item) => item?.name === value);
    const uniqueCode = new Set();
    const codeArr = arr.filter((item) => {
      const key = item?.productCode;
      if (uniqueCode.has(key)) return false;
      uniqueCode.add(key);
      return true;
    });
    setFilteredProductCode(codeArr);
  };

  const createExcelTemplate = () => {
    const workbook = new ExcelJS.Workbook();
    const consumableSheet = workbook.addWorksheet('Consumables');
    consumableSheet.getRow(1).height = 60;
    consumableSheet.columns = [
      { header: 'Consumable Category', key: 'category', width: 30 },
      { header: 'Name', key: 'name', width: 20 },
      { header: 'Product Code', key: 'productCode', width: 20 },
      { header: 'Initial Quantity', key: 'quantityInStock', width: 20 },
      { header: 'Safety Stock', key: 'safetyStock', width: 16 },
      { header: 'Unit', key: 'unit', width: 16 },
      { header: 'Criticality', key: 'criticality', width: 22 },
    ];
    consumableSheet.addRow([]);
    const materialSheet = workbook.addWorksheet('Material Consumption');
    materialSheet.getRow(1).height = 60;
    materialSheet.columns = [
      { header: 'Consumable Category', key: 'category', width: 30 },
      { header: 'Name', key: 'name', width: 20 },
      { header: 'Product Code', key: 'productCode', width: 20 },
      { header: 'Initial Quantity', key: 'totalMobStockQty', width: 20 },
      { header: 'Safety Stock', key: 'safetyStock', width: 16 },
      { header: 'Unit', key: 'unit', width: 16 },
      { header: 'Criticality', key: 'criticality', width: 22 },
    ];
    materialSheet.addRow([]);
    const critColIndex = 7;
    const applyCriticalityValidation = (sheet) => {
      for (let r = 2; r <= 500; r += 1) {
        sheet.getRow(r).getCell(critColIndex).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: ['"Critical,Medium,Normal"'],
          showErrorMessage: true,
          errorTitle: 'Criticality',
          error: 'Choose Critical, Medium, or Normal.',
        };
      }
    };
    applyCriticalityValidation(consumableSheet);
    applyCriticalityValidation(materialSheet);
    return workbook.xlsx.writeBuffer();
  };

  const handleDownloadTemplate = async () => {
    if (!isWriteAccess) return;
    try {
      const data = await createExcelTemplate();
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error('Failed to download template.');
    }
  };

  const openBulkUploadModal = () => {
    if (!selectedProject) {
      message.warning('Select a project to upload.');
      return;
    }
    setFileList([]);
    setDisableFileUpload(false);
    setBulkUploadModal(true);
  };

  const handleCancelBulkUpload = () => {
    setFileList([]);
    setBulkUploadModal(false);
    setDisableFileUpload(false);
  };

  const onFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setFileList([file]);
      setDisableFileUpload(true);
    }
  };

  const removeFile = () => {
    setFileList([]);
    setDisableFileUpload(false);
  };

  const handleBulkUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Please select a file to upload.');
      return;
    }
    await onUploadConsumableMaterialFiles({
      fileList,
      projectId: selectedProject.id,
    });
  };

  const handleAddOrUpdate = () => {
    if (
      !formCategory ||
      !formName ||
      !formProductCode ||
      formInitialQuantity === '' ||
      formSafetyStock === '' ||
      !formUnit
    ) {
      message.warning('Fill all the required fields.');
      return;
    }
    const initialQuantity =
      typeof formInitialQuantity === 'string'
        ? formInitialQuantity.trim()
        : formInitialQuantity;
    const safetyStock =
      typeof formSafetyStock === 'string' ? formSafetyStock.trim() : formSafetyStock;
    const payload = {
      category: formCategory,
      name: formName,
      productCode: formProductCode,
      projectId: selectedProject.id,
      quantityInStock: initialQuantity,
      safetyStock,
      unit: formUnit,
      criticality: formCriticality || undefined,
    };
    if (editRecord) {
      onUpdateConsumables({
        data: payload,
        consumableRecord: editRecord,
        selectedProject,
      });
    } else {
      onAddConsumables(payload);
    }
  };

  const showDeleteModal = (record) => {
    setRecordToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!recordToDelete || !selectedProject?.id) return;
    onDeleteConsumable({ id: recordToDelete.id, projectId: selectedProject.id });
  };

  const categoryOptions = categoryDataList.map((item) => ({
    value: item?.category,
    label: item?.category || '',
  }));
  const nameOptions = filteredCatg.map((item) => ({
    value: item?.name,
    label: item?.name || '',
  }));
  const productCodeOptions = filteredProductCode.map((item) => ({
    value: item?.productCode,
    label: item?.productCode || '',
  }));
  const unitOptions = unitDataList.map((item) => ({
    value: item?.unit,
    label: item?.unit || '',
  }));

  if (!selectedProject) {
    return (
      <div className="dpr-settings-consumables-tab">
        <div className="dpr-settings-consumables-card">
          <p style={{ margin: 0, color: '#64748B' }}>
            Select a project to manage consumables.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dpr-settings-consumables-tab">
      <div className="dpr-settings-consumables-card">
        <div className="dpr-settings-consumables-header">
          <div className="dpr-settings-consumables-header-left">
            <div className="dpr-settings-consumables-icon-wrap">
              <Package size={24} />
            </div>
            <div className="dpr-settings-consumables-header-content">
              <div className="dpr-settings-consumables-title-row">
                <h3 className="dpr-settings-consumables-title">List of Consumables</h3>
                <span className="dpr-settings-consumables-summary">
                  {list.length} item{list.length !== 1 ? 's' : ''}
                </span>
              </div>
              <p className="dpr-settings-consumables-description">
                Manage project consumables: add, edit, delete, and bulk upload.
              </p>
            </div>
          </div>
          <div className="dpr-settings-consumables-actions">
            <Button
              label="Download template"
              variant="tertiary"
              size="small"
              icon={Download}
              onClick={handleDownloadTemplate}
              disabled={!isWriteAccess}
            />
            <Button
              label="Bulk Upload"
              variant="tertiary"
              size="small"
              icon={Upload}
              onClick={openBulkUploadModal}
              disabled={!isWriteAccess}
            />
            <Button
              label="Add New"
              variant="primary"
              size="small"
              icon={Plus}
              onClick={openAddModal}
              disabled={!isWriteAccess}
            />
          </div>
        </div>

        <div className="dpr-settings-consumables-toolbar">
          <input
            type="text"
            className="dpr-settings-consumables-search"
            placeholder="Search Consumables"
            value={searchKey}
            onChange={(e) => {
              setSearchKey(e.target.value);
              setConsPage(1);
            }}
            aria-label="Search Consumables"
          />
        </div>

        <div className="dpr-settings-consumables-table-shell dpr-settings-pipelines-table-wrap">
          <div className="dpr-settings-consumables-table-wrap">
            <div className="dpr-settings-consumables-thead">
              <div className="dpr-settings-consumables-header-row dpr-settings-consumables-header-row--heat">
                <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-category">
                  {renderConsumableHeader(
                    'CONSUMABLE CATEGORY',
                    '__categoryDisplay',
                    '__categoryDisplay',
                    { headerAlign: 'left' }
                  )}
                </div>
                <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-name">
                  {renderConsumableHeader('NAME', '__nameDisplay', '__nameDisplay', { headerAlign: 'left' })}
                </div>
                <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-code">
                  {renderConsumableHeader(
                    'PRODUCT CODE',
                    '__productCodeDisplay',
                    '__productCodeDisplay',
                    { headerAlign: 'left' }
                  )}
                </div>
                <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-num">
                  {renderConsumableHeader('INITIAL QTY', '__qtyStock', 'quantityInStock')}
                </div>
                <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-num">
                  {renderConsumableHeader('USED QTY', '__qtyUsed', 'totalQuantityUsed')}
                </div>
                <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-num">
                  {renderConsumableHeader('QTY AVAILABLE', '__qtyRemaining', 'quantityRemaining')}
                </div>
                <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-num">
                  {renderConsumableHeader('SAFETY STOCK', '__safetyStock', 'safetyStock')}
                </div>
                <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-unit">
                  {renderConsumableHeader('UNIT', '__unit', '__unit')}
                </div>
                <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-criticality">
                  {renderConsumableHeader('CRITICALITY', '__criticality', '__criticality')}
                </div>
                <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-actions">
                  {renderConsumableHeader('ACTIONS', null, null, {
                    filterable: false,
                    headerAlign: 'center',
                  })}
                </div>
              </div>
            </div>

            {isConsumablesLoading ? (
              <div className="dpr-settings-consumables-loading">
                <Spinner size="medium" />
              </div>
            ) : list.length === 0 ? (
              <div className="dpr-settings-consumables-empty">
                <ProjectTabTableEmpty />
              </div>
            ) : (
              <div className="dpr-settings-consumables-rows-scroll">
                <div className="dpr-settings-consumables-rows">
                  {paginatedList.map((row) => (
                    <div key={row.id} className="dpr-settings-consumables-row">
                      <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-category">
                        {row.category ?? '—'}
                      </div>
                      <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-name">
                        {capitalizeWords(row.name) ?? '—'}
                      </div>
                      <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-code">
                        {(row.productCode || '').toUpperCase() || '—'}
                      </div>
                      <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-num">
                        {row.quantityInStock ?? '—'}
                      </div>
                      <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-num">
                        {row.totalQuantityUsed ?? '—'}
                      </div>
                      <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-num">
                        {row.quantityRemaining ?? '—'}
                      </div>
                      <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-num">
                        {row.safetyStock ?? '—'}
                      </div>
                      <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-unit">
                        {row.unit ?? '—'}
                      </div>
                      <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-criticality">
                        {row.criticality ?? '—'}
                      </div>
                      <div className="dpr-settings-consumables-cell dpr-settings-consumables-cell-actions">
                        <button
                          type="button"
                          className="dpr-settings-consumables-action-btn"
                          onClick={() => openEditModal(row)}
                          disabled={!isWriteAccess}
                          title="Edit"
                          aria-label="Edit"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          type="button"
                          className="dpr-settings-consumables-action-btn dpr-settings-consumables-action-delete"
                          onClick={() => showDeleteModal(row)}
                          disabled={!isWriteAccess}
                          title="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {!isConsumablesLoading && list.length > 0 ? (
            <div className="dpr-settings-consumables-pagination-bar">
              <Pagination
                className="heat-profile-pagination"
                current={consPage}
                pageSize={consPageSize}
                total={list.length}
                hideOnSinglePage={false}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                showTotal={(total, range) =>
                  total === 0 ? `0 of ${total}` : `${range[0]}–${range[1]} of ${total}`
                }
                onChange={(page, size) => {
                  setConsPage(page);
                  if (size != null && size !== consPageSize) {
                    setConsPageSize(size);
                    setConsPage(1);
                  }
                }}
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div
          className="dpr-settings-consumables-modal-backdrop"
          onClick={closeAddModal}
        >
          <div
            className="dpr-settings-consumables-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dpr-settings-consumables-modal-head">
              <h3 className="dpr-settings-consumables-modal-title">
                {editRecord ? 'Edit Consumable' : 'Add Consumable'}
              </h3>
              <button
                type="button"
                className="dpr-settings-consumables-modal-close"
                onClick={closeAddModal}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="dpr-settings-consumables-modal-body">
              <div className="dpr-settings-consumables-form-grid">
                <div className="dpr-settings-consumables-field">
                  <label>Consumable Category <span className="required">*</span></label>
                  <SimpleSelect
                    value={formCategory}
                    options={categoryOptions}
                    onChange={onCategoryChange}
                    placeholder="Consumable Category"
                    disabled={!!isConsumableCategoryLoading}
                    open={categoryOpen}
                    onOpenChange={setCategoryOpen}
                    containerRef={categoryRef}
                  />
                </div>
                <div className="dpr-settings-consumables-field">
                  <label>Name <span className="required">*</span></label>
                  <SimpleSelect
                    value={formName}
                    options={nameOptions}
                    onChange={onNameChange}
                    placeholder="Name"
                    disabled={!filteredCatg.length}
                    open={nameOpen}
                    onOpenChange={setNameOpen}
                    containerRef={nameRef}
                  />
                </div>
                <div className="dpr-settings-consumables-field">
                  <label>Product Code <span className="required">*</span></label>
                  <SimpleSelect
                    value={formProductCode}
                    options={productCodeOptions}
                    onChange={setFormProductCode}
                    placeholder="Product Code"
                    disabled={!formName}
                    open={productCodeOpen}
                    onOpenChange={setProductCodeOpen}
                    containerRef={productCodeRef}
                  />
                </div>
                <div className="dpr-settings-consumables-field">
                  <label>Initial Quantity <span className="required">*</span></label>
                  <input
                    type="text"
                    className="dpr-settings-consumables-input"
                    placeholder="Initial Quantity"
                    value={formInitialQuantity}
                    onChange={(e) => setFormInitialQuantity(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-consumables-field">
                  <label>Safety Stock <span className="required">*</span></label>
                  <input
                    type="text"
                    className="dpr-settings-consumables-input"
                    placeholder="Safety Stock"
                    value={formSafetyStock}
                    onChange={(e) => setFormSafetyStock(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-consumables-field">
                  <label>Unit <span className="required">*</span></label>
                  <SimpleSelect
                    value={formUnit}
                    options={unitOptions}
                    onChange={setFormUnit}
                    placeholder="Select Unit"
                    open={unitOpen}
                    onOpenChange={setUnitOpen}
                    containerRef={unitRef}
                  />
                </div>
                <div className="dpr-settings-consumables-field">
                  <label>Criticality</label>
                  <SimpleSelect
                    value={formCriticality}
                    options={[
                      { value: 'Critical', label: 'Critical' },
                      { value: 'Medium', label: 'Medium' },
                      { value: 'Normal', label: 'Normal' },
                    ]}
                    onChange={setFormCriticality}
                    placeholder="Select Criticality"
                    open={criticalityOpen}
                    onOpenChange={setCriticalityOpen}
                    containerRef={criticalityRef}
                    dropdownDirection="up"
                  />
                </div>
              </div>
            </div>
            <div className="dpr-settings-consumables-modal-foot">
              <Button
                label="Cancel"
                variant="tertiary"
                size="medium"
                onClick={closeAddModal}
              />
              <Button
                label={
                  editRecord
                    ? isConsumablesUpdating
                      ? 'Updating...'
                      : 'Update'
                    : isConsumablesLoading
                    ? 'Adding...'
                    : 'Add'
                }
                variant="primary"
                size="medium"
                onClick={handleAddOrUpdate}
                disabled={
                  isConsumablesUpdating ||
                  isConsumablesLoading ||
                  !formCategory ||
                  !formName ||
                  !formProductCode ||
                  formInitialQuantity === '' ||
                  formSafetyStock === '' ||
                  !formUnit
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {bulkUploadModal && (
        <div
          className="dpr-settings-consumables-modal-backdrop"
          onClick={handleCancelBulkUpload}
        >
          <div
            className="dpr-settings-consumables-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dpr-settings-consumables-modal-head">
              <h3 className="dpr-settings-consumables-modal-title">
                Upload Consumables Data
              </h3>
              <button
                type="button"
                className="dpr-settings-consumables-modal-close"
                onClick={handleCancelBulkUpload}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="dpr-settings-consumables-modal-body">
              <div className="dpr-settings-consumables-upload-area">
                <input
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={onFileSelect}
                  id="consumables-bulk-file"
                  style={{ display: 'none' }}
                />
                <label htmlFor="consumables-bulk-file" className="dpr-settings-consumables-upload-label">
                  Select File
                </label>
                {fileList.length > 0 && (
                  <div className="dpr-settings-consumables-file-name">
                    {fileList[0].name}
                    <button
                      type="button"
                      className="dpr-settings-consumables-remove-file"
                      onClick={removeFile}
                      aria-label="Remove file"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="dpr-settings-consumables-modal-foot">
              <Button
                label="Cancel"
                variant="tertiary"
                size="medium"
                onClick={handleCancelBulkUpload}
              />
              <Button
                label={
                  isUploadConsumablesAndMaterialConsumptions ? 'Uploading...' : 'Upload'
                }
                variant="primary"
                size="medium"
                onClick={handleBulkUpload}
                disabled={
                  isUploadConsumablesAndMaterialConsumptions || fileList.length === 0
                }
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        title="Delete Consumable"
        message={
          recordToDelete ? (
            <>
              Are you sure you want to delete{' '}
              <strong style={{ color: '#EF4444' }}>
                {recordToDelete.name} – {recordToDelete.productCode}
              </strong>{' '}
              consumable?
            </>
          ) : (
            'Are you sure you want to delete this consumable?'
          )
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setRecordToDelete(null);
        }}
        isOpen={isDeleteModalOpen}
        size="medium"
        confirmButtonProps={{
          style: { backgroundColor: '#EF4444', borderColor: '#EF4444' },
        }}
      />
    </div>
  );
};

const mapStateToProps = ({ consumableReducer, equip }) => ({
  consumables: get(consumableReducer, 'consumables', []),
  categoryData: get(consumableReducer, 'consumablesCategories', []),
  isConsumablesLoading: get(consumableReducer, 'isConsumablesLoading', false),
  isConsumableDeleteLoading: get(consumableReducer, 'isConsumableDeleteLoading', false),
  isUploadConsumablesAndMaterialConsumptions: get(
    consumableReducer,
    'isUploadConsumablesAndMaterialConsumptions',
    false
  ),
  isConsumablesUpdating: get(consumableReducer, 'isConsumablesUpdating', false),
  isConsumableCategoryLoading: get(
    consumableReducer,
    'isConsumablesCategoriesLoading',
    false
  ),
  projectTeamMembersAccessList: get(equip, 'projectTeamMembersAccessList', []),
  userAccessList: get(equip, 'userAccessList', []),
});

const mapDispatchToProps = {
  onFetchConsumables: fetchConsumables,
  onFetchConsumableCategories: fetchConsumableCategories,
  onAddConsumables: addConsumables,
  onUpdateConsumables: updateConsumables,
  onDeleteConsumable: deleteConsumable,
  onUploadConsumableMaterialFiles: uploadConsumablesAndMaterialFile,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DPRSettingsConsumablesTab);
